import { classNames } from "@frec-js/common-web";
import { FC, PropsWithChildren, useEffect, useState } from "react";

import { FeaturedArticle } from "../../components/BlogFeaturedArticle";
import { BlogListItem } from "../../components/BlogListItem";
import { PostType } from "../../utils/blogApi";

enum BlogMetadata {
  Featured = "featured",
  All = "All",
  Pinned = "pinned",
}

export const BlogList: FC<{ posts: PostType[] }> = ({ posts }) => {
  const featuredOnTop = posts.find((post) =>
    post.tags.includes(BlogMetadata.Featured),
  );
  const [filteredPosts, setFilteredPosts] = useState<PostType[]>(posts);
  const [selectedCategory, setSelectedCategory] = useState<string>(
    BlogMetadata.All,
  );
  const pinnedCategories = ["White paper", "Case study"];

  const allCategories = Array.from(
    new Set(posts.flatMap((post) => post.categories)),
  );

  const categories = pinnedCategories.filter(
    (category) =>
      category === BlogMetadata.All || allCategories.includes(category),
  );

  allCategories.forEach((category) => {
    if (!pinnedCategories.includes(category)) {
      categories.push(category);
    }
  });

  const sortAndFilterPosts = (category: string) => {
    const sortedAndFilteredPosts = posts
      .filter(
        (post) =>
          category === BlogMetadata.All || post.categories.includes(category),
      )
      .sort((a, b) => {
        // check if either post has a "pinned" tag
        const aIsPinned = a.tags.includes(BlogMetadata.Pinned);
        const bIsPinned = b.tags.includes(BlogMetadata.Pinned);

        // prioritize "pinned" blogs
        if (aIsPinned && !bIsPinned) {
          return -1; // a comes first if pinned and b is not
        } else if (!aIsPinned && bIsPinned) {
          return 1; // b comes first if pinned and a is not
        }
        // then sort by date published
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });

    setFilteredPosts(sortedAndFilteredPosts);
  };

  const filterPosts = (category: string) => {
    setSelectedCategory(category);
    sortAndFilterPosts(category);
  };

  useEffect(() => {
    sortAndFilterPosts(selectedCategory);
  }, [posts, selectedCategory]);

  return (
    <section className="grid grid-cols-[auto_minmax(0,86rem)_auto] grid-rows-[76px_auto_1px_auto_1px]">
      <div className="">{/* empty */}</div>
      <div className="dotted-v lg:mx-12 lg:border-x">{/* empty */}</div>
      <div className="">{/* empty */}</div>
      {/* row */}
      <div className="">{/* empty */}</div>
      <div className="dotted-v flex flex-col items-center lg:mx-12 lg:border-x">
        <div className="flex w-full max-w-5xl flex-col gap-12 px-6 py-12 lg:gap-[72px] lg:py-[72px]">
          {featuredOnTop && (
            <div>
              <FeaturedArticle
                key={featuredOnTop.slug}
                post={featuredOnTop}
                description={
                  <span>
                    Everything you need to know about what direct indexing is,
                    how it works, and whether it’s right for you.
                  </span>
                }
              ></FeaturedArticle>
            </div>
          )}
        </div>
      </div>
      <div className="">{/* empty */}</div>
      {/* row */}
      <div className="dotted-h col-span-3 border-b">{/* empty */}</div>
      {/* row */}
      <div className="">{/* empty */}</div>
      <div className="dotted-v mb-20 flex flex-col items-center lg:mx-12 lg:border-x">
        <div className="flex w-full max-w-5xl flex-col gap-12 px-6 py-12 lg:gap-[72px] lg:py-[72px]">
          {posts.length > 0 && (
            <div>
              <div className="mb-8 flex flex-wrap gap-2">
                <CategoryPill
                  onClick={() => filterPosts("All")}
                  active={selectedCategory === "All"}
                >
                  All
                </CategoryPill>
                {categories.map((category) => (
                  <CategoryPill
                    key={category}
                    onClick={() => filterPosts(category)}
                    active={category === selectedCategory}
                  >
                    {category === "Case study"
                      ? "Case studies"
                      : category === "White paper"
                        ? "White papers"
                        : category}
                  </CategoryPill>
                ))}
              </div>
              <div className="grid gap-x-11 gap-y-20 md:grid-cols-3">
                {filteredPosts.map((post) => (
                  <BlogListItem key={post.slug} post={post} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="">{/* empty */}</div>
      {/* row */}
      <div className="dotted-h col-span-3 border-b">{/* empty */}</div>
    </section>
  );
};

const CategoryPill: FC<
  PropsWithChildren<{
    category?: string;
    onClick?: () => void;
    active?: boolean;
  }>
> = ({ children, onClick, active }) => {
  return (
    <button
      onClick={onClick}
      className={classNames(
        "outline:none rounded-full border px-4 py-2 text-[14px] leading-5 font-medium",
        active
          ? "bg-frecBlack text-frecBeige border-frecBlack!"
          : "bg-frecBeige text-frecBlack border-frecDarkBeige! hover:bg-frecDarkBeige",
      )}
    >
      {children}
    </button>
  );
};
